/**
 * A Muse device straight over Web Bluetooth (desktop Chrome / Edge).
 *
 * Chrome lets a page open the Bluetooth chooser only inside a recent click, so
 * `connect` calls `requestDevice` before awaiting anything else. After that the
 * device object is held for the session: a dropped link reconnects silently.
 *
 * Unverified against hardware at the time of writing; the decoders follow the
 * community-documented Athena and original Muse protocols. Packet loss, unknown
 * tags and malformed packets are counted and shown in the panel's diagnostics
 * so a problem on the day is visible rather than silent.
 */
/// <reference path="./web-bluetooth.d.ts" />
import {
  CONTROL_CHARACTERISTIC, counterStep, DATA_CHARACTERISTIC, decodeBattery, decodeEeg, decodeNotification,
  decodeOptics, DEFAULT_PRESET, encodeCommand, MUSE_SERVICE, ReplyAssembler, startupSequence,
} from "./athena/protocol.ts";
import { CLOCK_BREAK, lostSubpackets, SensorClock } from "./athena/clock.ts";
import {
  decodeLegacyBattery, decodeLegacyEeg, decodeLegacyPpg, LegacyEegAssembler, LegacyPpgAssembler,
  LEGACY_EEG_CHARACTERISTICS, LEGACY_PPG_CHARACTERISTICS, LEGACY_PPG_PRESET, LEGACY_PRESET,
  LEGACY_TELEMETRY_CHARACTERISTIC, legacyStartupSequence,
} from "./legacy-protocol.ts";
import { EEG_RATE, emptyDiagnostics, OPTICS_RATE, SOURCE_LABELS, type MuseSource, type SourceDiagnostics, type SourceSink } from "./types.ts";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const RECONNECT_DELAYS_MS = [1000, 2000, 4000];
/** In optics8/optics16 the inner left/right NIR and IR channels are indices 4–7. */
const OPTICS_INNER = [4, 5, 6, 7];

export function bluetoothAvailable(): boolean {
  return typeof navigator !== "undefined" && Boolean((navigator as Navigator & { bluetooth?: unknown }).bluetooth);
}

/** Accumulates consecutive samples of one sensor into a block per notification. */
class BlockBuilder {
  #chunks: Float32Array[][] = [];
  #lostBefore = 0;
  get empty(): boolean { return this.#chunks.length === 0; }
  add(chunk: Float32Array[]): void { this.#chunks.push(chunk); }
  /** Samples were lost before the next chunk; the caller flushes first. */
  lose(samples: number): void { this.#lostBefore += samples; }
  take(): { samples: Float32Array[]; lostBefore: number } | null {
    if (!this.#chunks.length) return null;
    const channels = this.#chunks[0]!.length;
    const total = this.#chunks.reduce((sum, chunk) => sum + chunk[0]!.length, 0);
    const samples = Array.from({ length: channels }, () => new Float32Array(total));
    let offset = 0;
    for (const chunk of this.#chunks) {
      for (let c = 0; c < channels; c++) samples[c]!.set(chunk[c]!, offset);
      offset += chunk[0]!.length;
    }
    const lostBefore = this.#lostBefore;
    this.#chunks = [];
    this.#lostBefore = 0;
    return { samples, lostBefore };
  }
  pendingLoss(): number { return this.#lostBefore; }
}

export class BluetoothSource implements MuseSource {
  readonly deviceKind = "muse" as const;
  readonly kind = "bluetooth" as const;
  readonly label = SOURCE_LABELS.bluetooth;
  #protocol: "athena" | "legacy" | null = null;
  #batteryAvailable = false;
  #legacyPpgAvailable = false;
  readonly #athenaPreset: string;
  readonly #wait: (ms: number) => Promise<void>;
  #device: BluetoothDevice | null = null;
  #control: BluetoothRemoteGATTCharacteristic | null = null;
  #data: BluetoothRemoteGATTCharacteristic | null = null;
  #sink: SourceSink | null = null;
  #wanted = false;
  #generation = 0;
  #settingUp = false;
  #reconnecting = false;
  #paused = false;
  #subscriptions: { characteristic: BluetoothRemoteGATTCharacteristic; listener: EventListener }[] = [];
  #replies = new ReplyAssembler();
  #legacyEeg = new LegacyEegAssembler();
  #legacyPpg = new LegacyPpgAssembler();
  #eegClock = new SensorClock(EEG_RATE);
  #opticsClock = new SensorClock(OPTICS_RATE);
  #ppgClock = new SensorClock(OPTICS_RATE);
  #lastCounter: number | null = null;
  #lastEegSequence: number | null = null;
  #lastOpticsSequence: number | null = null;
  /** After a reconnect or resume, the first block of each sensor restarts the timeline. */
  #breakNext = { eeg: false, optics: false, ppg: false };
  #diagnostics: SourceDiagnostics = emptyDiagnostics();
  #info: Record<string, unknown> = {};

  constructor(options: { preset?: string; wait?: (ms: number) => Promise<void> } = {}) {
    this.#athenaPreset = options.preset ?? DEFAULT_PRESET;
    this.#wait = options.wait ?? sleep;
  }

  get protocol(): "athena" | "legacy" | null { return this.#protocol; }
  get preset(): string {
    if (this.#protocol !== "legacy") return this.#athenaPreset;
    return this.#legacyPpgAvailable ? LEGACY_PPG_PRESET : LEGACY_PRESET;
  }
  get capabilities(): { optics: boolean; battery: boolean; ppg: boolean } {
    return { optics: this.#protocol === "athena", battery: this.#batteryAvailable, ppg: this.#protocol === "athena" || this.#legacyPpgAvailable };
  }

  /** Firmware, serial and preset fields from the headset's control replies. */
  get info(): Record<string, unknown> { return { ...this.#info }; }

  async connect(sink: SourceSink): Promise<void> {
    if (!bluetoothAvailable()) throw new Error("This browser has no Web Bluetooth. Use desktop Chrome or Edge on localhost.");
    // First statement: the chooser needs the click's user activation.
    const request = navigator.bluetooth.requestDevice({
      filters: [{ services: [MUSE_SERVICE] }, { namePrefix: "Muse" }],
      optionalServices: [MUSE_SERVICE],
    });
    const generation = ++this.#generation;
    this.#sink = sink;
    sink.status({ phase: "requesting", detail: "choose the device in the browser's Bluetooth dialog", deviceName: null });
    let device: BluetoothDevice;
    try {
      device = await request;
    } catch (error) {
      const err = error as { name?: string; message?: string };
      throw new Error(err?.name === "NotFoundError" ? "No device was chosen." : err?.name === "NotAllowedError"
        ? "Bluetooth was not allowed. Click Connect again, and check that Chrome has Bluetooth permission in System Settings."
        : `Bluetooth: ${err?.message ?? String(error)}`);
    }
    if (generation !== this.#generation) throw new Error("Bluetooth connection was cancelled.");
    this.#device = device;
    this.#wanted = true;
    this.#paused = false;
    this.#info = {};
    device.addEventListener("gattserverdisconnected", this.#onDisconnected);
    try {
      await this.#setup(true, generation);
    } catch (error) {
      if (generation === this.#generation) {
        this.#wanted = false;
        device.removeEventListener("gattserverdisconnected", this.#onDisconnected);
        this.#releaseGatt();
      }
      throw error;
    }
  }

  async #setup(fresh: boolean, generation: number): Promise<void> {
    const device = this.#device;
    if (!device?.gatt) throw new Error("The device exposes no GATT server.");
    const name = device.name ?? "Device";
    const check = () => {
      if (!this.#wanted || generation !== this.#generation) throw new Error("Bluetooth connection was cancelled.");
      if (!device.gatt?.connected) throw new Error("The Bluetooth link was lost during setup. Check the device and connect again.");
    };
    this.#settingUp = true;
    try {
      this.#sink?.status({ phase: "connecting", detail: "connecting to the device", deviceName: name });
      await device.gatt.connect();
      check();
      let service: BluetoothRemoteGATTService;
      try {
        service = await device.gatt.getPrimaryService(MUSE_SERVICE);
      } catch (error) {
        if ((error as { name?: string }).name === "NotFoundError") throw new Error("The device does not expose the required service.");
        throw error;
      }
      check();
      // Inspect the actual GATT profile; a transport/permission error must not
      // be misreported as an old device merely because a lookup failed.
      const characteristics = await service.getCharacteristics();
      check();
      const byUuid = new Map(characteristics.map((characteristic) => [characteristic.uuid.toLowerCase(), characteristic]));
      this.#control = byUuid.get(CONTROL_CHARACTERISTIC) ?? null;
      if (!this.#control) throw new Error("The device has no required control characteristic.");
      this.#data = byUuid.get(DATA_CHARACTERISTIC) ?? null;
      const legacy = LEGACY_EEG_CHARACTERISTICS.map((uuid) => byUuid.get(uuid));
      if (!this.#data && legacy.some((characteristic) => !characteristic)) {
        throw new Error("The device has no supported data profile. Try the local app bridge.");
      }
      this.#protocol = this.#data ? "athena" : "legacy";
      const ppg = LEGACY_PPG_CHARACTERISTICS.map((uuid) => byUuid.get(uuid));
      // The original Muse profile exposes a separate three-channel PPG signal.
      // Only select its enabling preset when the complete GATT profile exists.
      this.#legacyPpgAvailable = this.#protocol === "legacy" && ppg.every(Boolean);
      const telemetry = byUuid.get(LEGACY_TELEMETRY_CHARACTERISTIC);
      this.#batteryAvailable = this.#protocol === "athena" || Boolean(telemetry);
      this.#info.protocol = this.#protocol;
      this.#info.ppg = this.capabilities.ppg;
      this.#replies = new ReplyAssembler();
      this.#resetTiming();
      if (fresh) this.#diagnostics = emptyDiagnostics();
      if (fresh && this.#legacyPpgAvailable) {
        this.#diagnostics.ppgPackets = 0;
        this.#diagnostics.lastPpgPacketAt = null;
        this.#sink?.diagnostics({ ...this.#diagnostics });
      }
      await this.#subscribe(this.#control, this.#onControl);
      check();
      if (this.#data) {
        await this.#subscribe(this.#data, this.#onData);
        check();
      } else {
        for (let channel = 0; channel < legacy.length; channel++) {
          await this.#subscribe(legacy[channel]!, (event) => this.#onLegacyEeg(channel, event));
          check();
        }
        if (this.#legacyPpgAvailable) {
          for (let channel = 0; channel < ppg.length; channel++) {
            await this.#subscribe(ppg[channel]!, (event) => this.#onLegacyPpg(channel, event));
            check();
          }
        }
        if (telemetry) {
          await this.#subscribe(telemetry, this.#onLegacyTelemetry);
          check();
        }
      }
      this.#sink?.status({ phase: "configuring", detail: `applying preset ${this.preset}`, deviceName: name });
      const commands = this.#protocol === "legacy" ? legacyStartupSequence(this.#legacyPpgAvailable) : startupSequence(this.preset);
      for (const [command, pause] of commands) {
        check();
        await this.#send(command);
        if (pause) await this.#wait(pause);
      }
      check();
      if (this.#paused) await this.#send("h");
      check();
      this.#sink?.status({ phase: this.#paused ? "halted" : "streaming", detail: this.#paused ? "paused" : this.#streamDetail(), deviceName: name });
    } catch (error) {
      if (generation === this.#generation) this.#releaseGatt();
      throw error;
    } finally {
      if (generation === this.#generation) this.#settingUp = false;
    }
  }

  #streamDetail(): string {
    return this.#protocol === "legacy" ? `preset ${this.preset} · signal (4 channels, 256 Hz)${this.#legacyPpgAvailable ? " + PPG (3 channels, 64 Hz)" : ""}` : `preset ${this.preset} · signal + optics`;
  }

  async #subscribe(characteristic: BluetoothRemoteGATTCharacteristic, listener: EventListener): Promise<void> {
    characteristic.addEventListener("characteristicvaluechanged", listener);
    this.#subscriptions.push({ characteristic, listener });
    await characteristic.startNotifications();
  }

  #clearNotifications(): void {
    for (const { characteristic, listener } of this.#subscriptions) characteristic.removeEventListener("characteristicvaluechanged", listener);
    this.#subscriptions = [];
    this.#control = this.#data = null;
  }

  #releaseGatt(): void {
    this.#clearNotifications();
    try { this.#device?.gatt?.disconnect(); } catch {}
  }

  #resetTiming(): void {
    this.#breakNext = { eeg: true, optics: true, ppg: true };
    this.#eegClock.reset();
    this.#opticsClock.reset();
    this.#ppgClock.reset();
    this.#lastCounter = this.#lastEegSequence = this.#lastOpticsSequence = null;
    this.#legacyEeg.reset();
    this.#legacyPpg.reset();
  }

  async #send(command: string): Promise<void> {
    const control = this.#control;
    if (!control) return;
    const frame = encodeCommand(command);
    if (control.properties?.writeWithoutResponse && typeof control.writeValueWithoutResponse === "function") await control.writeValueWithoutResponse(frame);
    else if (typeof control.writeValueWithResponse === "function") await control.writeValueWithResponse(frame);
    else await control.writeValue(frame);
  }

  #onControl = (event: Event): void => {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    if (!value) return;
    for (const reply of this.#replies.push(new Uint8Array(value.buffer, value.byteOffset, value.byteLength))) {
      Object.assign(this.#info, reply);
      if (typeof reply.bp === "number" && Number.isFinite(reply.bp)) this.#sink?.battery(reply.bp);
    }
  };

  #onData = (event: Event): void => {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    const sink = this.#sink;
    if (!value || !sink || this.#paused) return;
    const arrival = Date.now();
    const { packets, unknownTags, malformed } = decodeNotification(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    const eeg = new BlockBuilder();
    const optics = new BlockBuilder();
    const flush = (builder: BlockBuilder, clock: SensorClock, kind: "eeg" | "optics") => {
      const block = builder.take();
      if (!block) return;
      const stamp = clock.stamp(block.samples[0]!.length, arrival, block.lostBefore);
      const gapBefore = this.#breakNext[kind] ? CLOCK_BREAK : stamp.gapBefore;
      this.#breakNext[kind] = false;
      if (kind === "eeg") sink.eeg({ t0: stamp.t0, rate: EEG_RATE, samples: block.samples.slice(0, 4), gapBefore });
      else sink.optics({ t0: stamp.t0, rate: OPTICS_RATE, samples: block.samples, gapBefore });
    };
    for (const packet of packets) {
      let packetsLost = false;
      if (this.#lastCounter !== null) {
        const step = counterStep(this.#lastCounter, packet.counter);
        if (step !== 1) {
          packetsLost = true;
          this.#diagnostics.lostPackets += Math.max(0, step - 1);
        }
      }
      this.#lastCounter = packet.counter;
      this.#diagnostics.packets++;
      for (const sub of packet.subpackets) {
        if (sub.kind === "eeg") {
          const lost = lostSubpackets(this.#lastEegSequence, sub.sequence, packetsLost) * sub.spec.samples;
          this.#lastEegSequence = sub.sequence;
          const samples = decodeEeg(sub);
          if (!samples) continue;
          if (lost) { flush(eeg, this.#eegClock, "eeg"); eeg.lose(lost); }
          eeg.add(samples.slice(0, 4));
        } else if (sub.kind === "optics") {
          const lost = lostSubpackets(this.#lastOpticsSequence, sub.sequence, packetsLost) * sub.spec.samples;
          this.#lastOpticsSequence = sub.sequence;
          const samples = decodeOptics(sub);
          if (!samples) continue;
          if (lost) { flush(optics, this.#opticsClock, "optics"); optics.lose(lost); }
          optics.add(samples.length === 4 ? samples : OPTICS_INNER.map((index) => samples[index]!));
        } else if (sub.kind === "battery") {
          const percent = decodeBattery(sub);
          if (percent !== null) sink.battery(percent);
        }
      }
    }
    flush(eeg, this.#eegClock, "eeg");
    flush(optics, this.#opticsClock, "optics");
    this.#diagnostics.unknownTags += unknownTags;
    this.#diagnostics.parseErrors += malformed;
    this.#diagnostics.lastPacketAt = arrival;
    sink.diagnostics({ ...this.#diagnostics });
  };

  #onLegacyEeg(channel: number, event: Event): void {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    const sink = this.#sink;
    if (!value || !sink || this.#paused) return;
    const arrival = Date.now();
    const packet = decodeLegacyEeg(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    this.#diagnostics.packets++;
    this.#diagnostics.lastPacketAt = arrival;
    if (!packet) this.#diagnostics.parseErrors++;
    else for (const block of this.#legacyEeg.push(channel, packet, arrival)) {
      const stamp = this.#eegClock.stamp(block.samples[0]!.length, block.arrival, block.lostSamples);
      const gapBefore = this.#breakNext.eeg ? CLOCK_BREAK : stamp.gapBefore;
      this.#breakNext.eeg = false;
      this.#diagnostics.lostPackets += block.lostPackets;
      sink.eeg({ t0: stamp.t0, rate: EEG_RATE, samples: block.samples, gapBefore });
    }
    sink.diagnostics({ ...this.#diagnostics });
  }

  #onLegacyTelemetry = (event: Event): void => {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    if (!value) return;
    const percent = decodeLegacyBattery(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    if (percent !== null) this.#sink?.battery(percent);
    else {
      this.#diagnostics.parseErrors++;
      this.#sink?.diagnostics({ ...this.#diagnostics });
    }
  };

  #onLegacyPpg(channel: number, event: Event): void {
    const value = (event.target as BluetoothRemoteGATTCharacteristic).value;
    const sink = this.#sink;
    if (!value || !sink || this.#paused) return;
    const arrival = Date.now();
    const packet = decodeLegacyPpg(new Uint8Array(value.buffer, value.byteOffset, value.byteLength));
    this.#diagnostics.packets++;
    this.#diagnostics.lastPacketAt = arrival;
    if (!packet) this.#diagnostics.parseErrors++;
    else {
      this.#diagnostics.ppgPackets = (this.#diagnostics.ppgPackets ?? 0) + 1;
      this.#diagnostics.lastPpgPacketAt = arrival;
      for (const block of this.#legacyPpg.push(channel, packet, arrival)) {
        const stamp = this.#ppgClock.stamp(block.samples[0]!.length, block.arrival, block.lostSamples);
        const gapBefore = this.#breakNext.ppg ? CLOCK_BREAK : stamp.gapBefore;
        this.#breakNext.ppg = false;
        this.#diagnostics.lostPackets += block.lostPackets;
        sink.ppg?.({ t0: stamp.t0, rate: OPTICS_RATE, samples: block.samples, gapBefore });
      }
    }
    sink.diagnostics({ ...this.#diagnostics });
  }

  #onDisconnected = (): void => {
    this.#clearNotifications();
    if (!this.#wanted || this.#settingUp || this.#reconnecting) return;
    void this.#reconnect();
  };

  async #reconnect(): Promise<void> {
    this.#reconnecting = true;
    const generation = this.#generation;
    const name = this.#device?.name ?? "Device";
    try {
      for (const delay of RECONNECT_DELAYS_MS) {
        if (!this.#wanted || generation !== this.#generation) return;
        this.#sink?.status({ phase: "reconnecting", detail: `link lost · retrying in ${delay / 1000} s`, deviceName: name });
        await this.#wait(delay);
        if (!this.#wanted || generation !== this.#generation) return;
        try {
          await this.#setup(false, generation);
          return;
        } catch {
          // Try again after the next delay.
        }
      }
      this.#wanted = false;
      this.#device?.removeEventListener("gattserverdisconnected", this.#onDisconnected);
      this.#sink?.status({ phase: "error", detail: "The device did not reconnect. Check it is on and nearby, then connect again.", deviceName: name });
    } finally {
      this.#reconnecting = false;
    }
  }

  async halt(): Promise<void> {
    this.#paused = true;
    await this.#send("h").catch(() => {});
    this.#sink?.status({ phase: "halted", detail: "paused", deviceName: this.#device?.name ?? null });
  }

  async resume(): Promise<void> {
    this.#resetTiming();
    this.#paused = false;
    const commands: [string, number][] = this.#protocol === "legacy" ? [["d", 0]] : startupSequence(this.preset).slice(3);
    for (const [command, pause] of commands) {
      await this.#send(command);
      if (pause) await this.#wait(pause);
    }
    this.#sink?.status({ phase: "streaming", detail: this.#streamDetail(), deviceName: this.#device?.name ?? null });
  }

  async disconnect(): Promise<void> {
    this.#wanted = false;
    ++this.#generation;
    await this.#send("h").catch(() => {});
    this.#device?.removeEventListener("gattserverdisconnected", this.#onDisconnected);
    this.#releaseGatt();
    this.#device = null;
    this.#sink?.status({ phase: "closed", detail: "", deviceName: null });
    this.#sink = null;
  }
}
